import {
  Button,
  Slide,
  Snackbar,
  SnackbarCloseReason,
  SnackbarOrigin,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, lighten, Palette } from '@mui/material/styles';
import React, { useCallback, useMemo, useRef } from 'react';

import { NOTICE_SNACKBAR_TOP_OFFSET } from '../../../constants/country';
import {
  SNACKBAR_ACTION_TINT,
  SNACKBAR_AUTO_HIDE_MS,
  SNACKBAR_BOTTOM_TRANSITION,
  SNACKBAR_DEFAULT_BOTTOM,
  SNACKBAR_FOOTER_CLEARANCE,
  SNACKBAR_TRANSITION_DURATION,
  SNACKBAR_TRANSITION_EASING,
  SNACKBAR_Z_INDEX,
} from '../../../constants/ui';
import { useFooterVisibility } from '../../../contexts/FooterContext';

interface IProps {
  open: boolean;
  message: string;
  onClose: () => void;
  actionLabel?: string;
  onAction?: () => void;
  topOffset?: number;
  noticeId?: number | string;
}

const bottomOrigin: SnackbarOrigin = { vertical: 'bottom', horizontal: 'left' };

const topOrigin: SnackbarOrigin = { vertical: 'top', horizontal: 'center' };

const transitionSlots = { transition: Slide };

const getActionColor = (palette: Palette) => lighten(palette.primary.main, SNACKBAR_ACTION_TINT);

const actionButtonSx: SxProps<Theme> = {
  color: ({ palette }) => getActionColor(palette),
  '&:hover': { backgroundColor: ({ palette }) => alpha(getActionColor(palette), 0.08) },
};

export const NoticeSnackbar: React.FC<IProps> = ({
  open,
  message,
  onClose,
  actionLabel,
  onAction,
  topOffset,
  noticeId,
}) => {
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('sm'));
  const { isFooterVisible } = useFooterVisibility();

  const isTop = isMobileViewport && topOffset !== undefined;
  const lastMessageRef = useRef(message);
  const lastKeyRef = useRef<number | string>(noticeId ?? message);

  if (message) {
    lastMessageRef.current = message;
  }

  if (noticeId !== undefined) {
    lastKeyRef.current = noticeId;
  } else if (message) {
    lastKeyRef.current = message;
  }

  const shownMessage = message || lastMessageRef.current;

  const transitionSlotProps = useMemo(
    () => ({
      transition: { direction: isTop ? ('down' as const) : ('up' as const), easing: SNACKBAR_TRANSITION_EASING },
    }),
    [isTop],
  );

  const snackbarSx: SxProps<Theme> = useMemo(
    () =>
      isTop
        ? { zIndex: SNACKBAR_Z_INDEX, top: (topOffset ?? 0) + NOTICE_SNACKBAR_TOP_OFFSET }
        : {
            zIndex: SNACKBAR_Z_INDEX,
            bottom: isFooterVisible
              ? { xs: SNACKBAR_FOOTER_CLEARANCE, sm: SNACKBAR_FOOTER_CLEARANCE }
              : SNACKBAR_DEFAULT_BOTTOM,
            transition: SNACKBAR_BOTTOM_TRANSITION,
          },
    [isTop, topOffset, isFooterVisible],
  );

  const handleClose = useCallback(
    (_event: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
      if (reason !== 'clickaway') {
        onClose();
      }
    },
    [onClose],
  );

  const handleAction = useCallback(() => {
    onAction?.();
    onClose();
  }, [onAction, onClose]);

  const action = useMemo(
    () =>
      actionLabel ? (
        <Button size="small" onClick={handleAction} sx={actionButtonSx}>
          {actionLabel}
        </Button>
      ) : undefined,
    [actionLabel, handleAction],
  );

  return (
    <Snackbar
      key={lastKeyRef.current}
      open={open}
      message={shownMessage}
      action={action}
      autoHideDuration={SNACKBAR_AUTO_HIDE_MS}
      transitionDuration={SNACKBAR_TRANSITION_DURATION}
      slots={transitionSlots}
      slotProps={transitionSlotProps}
      anchorOrigin={isTop ? topOrigin : bottomOrigin}
      onClose={handleClose}
      sx={snackbarSx}
    />
  );
};
